import {
    React,
    useState,
    IconButton,
    Typography, Toaster,
    formatCurrency,
    formatPercentage,
    Paper, Grid2,
    AddIcon, RemoveIcon,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    Box, Button
} from '../../../utils/globalImports';

import MoreVertIcon from '@mui/icons-material/MoreVert'; 
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import { Historic } from '../../../interfaces/forecast.interface';

interface ModalProps {
    data: Historic[];
}


const commonRowHeader = {
    '& td': {
        fontSize: '14px',
        fontWeight: 600,
        border: '1px solid rgba(0, 0, 0, 0.04)',
        color: '#1e66ab', 
        padding: '8px'
    },
};


const commonRowStyle = {
    '& td': {
        fontSize: '14px',
        border: '2px solid rgba(0, 0, 0, 0.04)',
        color: '#1e66ab', 
        padding: '8px'
    },
};

const ProjectHistoric: React.FC<ModalProps> = ({ data }) => {
    const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

    const toggleExpand = (index: number) => {
        setExpandedRows((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    return (
        <Box sx={{ p: 3, flex: 1 }}>
            <Grid2 container spacing={3}>
                {data.map((entry, index) => {
                    const isExpanded = expandedRows[index];

                    
                    let diffBg = 'rgba(169, 201, 81, 0.14)'; 
                    let diffColor = 'rgba(150, 193, 33, 0.75)'; 

                    if (entry.difference > 3500) {
                        diffBg = '#FFEAEA'; 
                        diffColor = '#FF6961'; 
                    } else if (entry.difference > 0 && entry.difference <= 3500) {
                        diffBg = '#FFF3E0'; 
                        diffColor = '#FFD580'; 
                    }

                    return (
                        <Grid2 key={index} size={{ xs: 12, md: 6, lg: 4 }}>
                            <Paper elevation={1} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #eaeaea', backgroundColor: '#fff' }}>
                                
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CalendarTodayIcon sx={{ color: '#1e66ab', fontSize: 18 }} /> 
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#000' }}> 
                                            {entry.forecastMonth}
                                        </Typography>
                                    </Box>
                                </Box>

                                
                                <Box display="flex" sx={{ border: '1px solid #f0f0f4', borderRadius: 2, overflow: 'hidden', mb: 1, textAlign: 'center' }}>
                                    <Box flex={1} sx={{ p: 1.5,backgroundColor: "#f5f5f580", borderRight: '1px solid #f0f0f4', }}>
                                        <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 600, display: 'block', mb: 0.5 }}>Invoiced</Typography>
                                        <Typography variant="h6" sx={{ fontSize: '18px',fontWeight: 500, color: '#1e66ab' }}>{formatCurrency(entry.totalInvoiceValue)}</Typography>
                                    </Box>
                                    <Box flex={1} sx={{ p: 1.5, borderRight: '1px solid #f0f0f4', backgroundColor: "#f5f5f580", }}>
                                        <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 600, display: 'block', mb: 0.5 }}>Forecast</Typography>
                                        <Typography variant="h6" sx={{ fontSize: '18px',fontWeight: 500, color: '#1e66ab' }}>{formatCurrency(entry.totalForecast)}</Typography>
                                    </Box>
                                    <Box flex={1} sx={{ p: 1.5, backgroundColor: diffBg }}>
                                        <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 600, display: 'block', mb: 0.5 }}>Difference</Typography>
                                        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 500, color: diffColor }}>{formatCurrency(entry.difference)}</Typography>
                                    </Box>
                                </Box>

                                {/* Lower Breakdowns Table */}
                                {isExpanded && (
                                    <TableContainer sx={{ border: 'none', mt: 1.5 }}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow sx={commonRowHeader}>
                                                    <TableCell align="center">KPI from</TableCell>
                                                    <TableCell align="center">Forecast</TableCell>
                                                    <TableCell align="center">MCI(Month Change Index)</TableCell>
                                                </TableRow>
                                                <TableRow sx={commonRowStyle}>
                                                    <TableCell align="center" >12w</TableCell>
                                                    <TableCell align="center">{entry.twelveWeeksInvoice || 0}</TableCell>
                                                    <TableCell align="center">{formatPercentage(entry.twelveWeekMegOfChange || 0)}</TableCell>
                                                </TableRow>
                                                <TableRow sx={commonRowStyle}>
                                                    <TableCell align="center" >10w</TableCell>
                                                    <TableCell align="center">{entry.tenWeeksInvoice || 0}</TableCell>
                                                    <TableCell align="center">{formatPercentage(entry.tenWeekMegOfChange || 0)}</TableCell>
                                                </TableRow>
                                                <TableRow sx={commonRowStyle}>
                                                    <TableCell align="center" >8w</TableCell>
                                                    <TableCell align="center">{entry.eightWeeksInvoice || 0}</TableCell>
                                                    <TableCell align="center">{formatPercentage(entry.eightWeekMegOfChange || 0)}</TableCell>
                                                </TableRow>
                                                <TableRow sx={commonRowStyle}>
                                                    <TableCell align="center" >6w</TableCell>
                                                    <TableCell align="center">{entry.sixWeeksInvoice || 0}</TableCell>
                                                    <TableCell align="center">{formatPercentage(entry.sixWeekMegOfChange || 0)}</TableCell>
                                                </TableRow>
                                                <TableRow sx={commonRowStyle}>
                                                    <TableCell align="center" >4w</TableCell>
                                                    <TableCell align="center">{entry.fourWeeksInvoice || 0}</TableCell>
                                                    <TableCell align="center">{formatPercentage(entry.fourWeekMegOfChange || 0)}</TableCell>
                                                </TableRow>
                                                <TableRow sx={commonRowStyle}>
                                                    <TableCell align="center" >2w</TableCell>
                                                    <TableCell align="center">{entry.twoWeeksInvoice || 0}</TableCell>
                                                    <TableCell align="center">{formatPercentage(entry.twoWeekMegOfChange || 0)}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                                    <IconButton size="small" onClick={() => toggleExpand(index)} sx={{ color: '#1e66ab', backgroundColor: "#f5f5f580"  }}>
                                        {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                    </IconButton>
                                </Box>

                            </Paper>
                        </Grid2>
                    );
                })}
            </Grid2>
        </Box>
    );
};

export default ProjectHistoric;